import React, { useState, useEffect, useCallback } from "react";
import apiFetch from "./utils/api";
import Typeahead from "./components/Typeahead";

function FormatDateTime({ dt }) {
  try {
    const d = new Date(dt);
    return (
      <p>
        {d.toLocaleDateString("en-GB")}
        <br />
        <b>
          {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </b>
      </p>
    );
  } catch (e) {
    return dt;
  }
}

function calculateHoursDifference(start, end) {
  const diff = end.getTime() - start.getTime();
  return Math.round(diff / 900000) / 4;
}

export default function OutlookPage({ user }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [statusMsg, setStatusMsg] = useState("");
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedMap, setSelectedMap] = useState({}); // eventIndex -> {project, activity, checked}
  const [selectedWeekStart, setSelectedWeekStart] = useState(new Date()); // week start date for filtering

  useEffect(() => {
    (async () => {
      try {
        const p = await apiFetch("/api/projects");
        setProjects(Array.isArray(p.projects) ? p.projects : p);
      } catch (e) {}
      try {
        const a = await apiFetch("/api/activities");
        setActivities(Array.isArray(a.activities) ? a.activities : a);
      } catch (e) {}
    })();
  }, []);

  const getWeekMonday = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekRangeDisplay = () => {
    const monday = getWeekMonday(selectedWeekStart);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return `${monday.toLocaleDateString("en-GB")} - ${sunday.toLocaleDateString("en-GB")}`;
  };

  const fetchWeek = useCallback(async (weekStart = null) => {
    setLoading(true);
    setStatusMsg("Loading...");
    try {
      // Use provided date or default to current week Monday start
      const baseDate = weekStart || new Date();
      const day = baseDate.getDay();
      const diff = baseDate.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(baseDate);
      monday.setDate(diff);
      monday.setHours(6, 0, 0);
      const start = monday.toISOString();
      const sunday = new Date(monday);
      let end = sunday.setDate(monday.getDate() + 7);
      end = sunday.toISOString();

      console.log(
        `/api/auth/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      );

      const resp = await apiFetch(
        `/api/auth/calendar?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      );

      if (!(resp?.value || resp?.error) && resp !== null)
        window.location.href = resp;

      console.log(resp);
      // If the helper returned an error object, surface it for debugging
      if (resp && resp.error) {
        const details = resp.details
          ? ` — ${JSON.stringify(resp.details)}`
          : "";
        setStatusMsg(`API error: ${resp.error}${details}`);
        setEvents(resp.value || []);
      } else {
        let evs = (resp && resp.value) || [];
        // ensure we only display events within the requested week
        try {
          evs = evs.filter((ev) => {
            try {
              const s = new Date(ev.start.dateTime);
              console.log(monday, s, sunday);
              return s >= monday && s < sunday;
            } catch (e) {
              return false;
            }
          });
          evs = evs.sort((a, b) => {
            return Date.parse(a.start.dateTime) - Date.parse(b.start.dateTime);
          });
        } catch (e) {
          // ignore filtering errors and fall back to raw events
        }

        setEvents(evs);
        if (!evs || evs.length === 0) {
          const baseDate = weekStart || new Date();
          const day = baseDate.getDay();
          const diff = baseDate.getDate() - day + (day === 0 ? -6 : 1);
          const mon = new Date(baseDate);
          mon.setDate(diff);
          const sun = new Date(mon);
          sun.setDate(mon.getDate() + 6);
          const weekStr = `${mon.toLocaleDateString("en-GB")} - ${sun.toLocaleDateString("en-GB")}`;
          setStatusMsg(
            `No events found for the selected inbox/date range (week of ${weekStr}).`,
          );
        } else {
          setSelectedMap({});
          setStatusMsg("");
        }
      }
    } catch (e) {
      console.error("FetchWeek error:", e);
      setStatusMsg(`Fetch failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => await fetchWeek(selectedWeekStart))();
  }, [selectedWeekStart, fetchWeek]);

  const previousWeek = () => {
    const newDate = new Date(selectedWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setSelectedWeekStart(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(selectedWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setSelectedWeekStart(newDate);
  };

  const handleWeekDateChange = (dateString) => {
    const newDate = new Date(dateString);
    if (!isNaN(newDate.getTime())) {
      setSelectedWeekStart(newDate);
    }
  };

  const toggleChecked = (idx) => {
    setSelectedMap((m) => ({
      ...m,
      [idx]: { ...(m[idx] || {}), checked: !(m[idx] || {}).checked },
    }));
  };

  const setProjectFor = (idx, project) => {
    setSelectedMap((m) => ({ ...m, [idx]: { ...(m[idx] || {}), project } }));
  };

  const setActivityFor = (idx, activity) => {
    setSelectedMap((m) => ({ ...m, [idx]: { ...(m[idx] || {}), activity } }));
  };

  const setHoursFor = (idx, hours) => {
    setSelectedMap((m) => ({ ...m, [idx]: { ...(m[idx] || {}), hours } }));
  };

  const setSubjectFor = (idx, subject) => {
    setSelectedMap((m) => ({ ...m, [idx]: { ...(m[idx] || {}), subject } }));
  };

  const saveSelected = async () => {
    // build records from checked events
    setStatusMsg("Saving");

    const recs = [];
    for (let i = 0; i < events.length; i++) {
      const s = selectedMap[i];
      if (!s || !s.checked) continue;
      const ev = events[i];
      console.log(s);
      recs.push({
        date: new Date(ev.start.dateTime).toISOString(), //.slice(0, 10),
        hours: s.hours || calculateHoursDifference(
          new Date(ev.start.dateTime),
          new Date(ev.end.dateTime),
        ), // Handles quarter hour accuracy
        project_code: s.project ? s.project.id : null,
        sub_code: s.activity ? s.activity.id : null,
        division: s.project ? s.project.division : null,
        notes: typeof s.subject === "string" ? s.subject : ev.subject || null,
      });
    }

    console.log(recs);

    if (recs.length === 0) {
      setStatusMsg("No entries to save");
      return
    };
    try {
      const resp = await apiFetch("/api/timesheets/me", {
        method: "POST",
        body: { records: recs },
      });
      // server returns { ok: true, inserted: N } on success
      const inserted = resp && resp.inserted ? resp.inserted : 0;
      setStatusMsg("Saved " + inserted + " records");
      setTimeout(() => setStatusMsg(""), 3000);

      // remove saved events from the UI. We remove the first N matching checked records
      if (inserted > 0) {
        // Build a new events array excluding the checked items that were submitted
        //const remaining = [];
        //const newSelected = {};
        //let toRemove = inserted;
        for (let i = 0; i < events.length; i++) {
          const s = selectedMap[i];
          if (s && s.checked) {
            // skip (remove) this event
            selectedMap[i].checked = false;
            events[i].timesheetExisting = true;
            //toRemove -= 1;
            continue;
          }
          // keep this event; remap selectedMap indexes to new positions
          //const newIdx = remaining.length;
          //remaining.push(events[i]);
          //if (selectedMap[i]) {
            //newSelected[newIdx] = { ...selectedMap[i], checked: false };
          //}
        }
        //setEvents(remaining);
        //setSelectedMap(newSelected);
      }
      setSelectedWeekStart(new Date(selectedWeekStart.getTime() + 1));
    } catch (e) {
      setStatusMsg("Save failed: " + (e.message || e));
    }
  };

  return (
    <div style={{ padding: 12 }}>
      <h3>Outlook</h3>
      <div
        style={{
          marginBottom: 8,
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={previousWeek} disabled={loading}>
            ← Previous Week
          </button>
          <input
            type="date"
            value={selectedWeekStart.toISOString().split("T")[0]}
            onChange={(e) => handleWeekDateChange(e.target.value)}
            disabled={loading}
            style={{ padding: 6 }}
          />
          <button onClick={nextWeek} disabled={loading}>
            Next Week →
          </button>
          <span style={{ fontWeight: "bold", minWidth: 200 }}>
            Week: {getWeekRangeDisplay()}
          </span>
        </div>
        <button onClick={() => fetchWeek(selectedWeekStart)} disabled={loading}>
          Refresh
        </button>
        <button onClick={saveSelected} style={{ marginLeft: 8 }}>
          Save selected to Timesheets
        </button>
        {statusMsg && (
          <label
            style={{
              marginLeft: 8,
              color: statusMsg.startsWith("Sav") ? "green" : "darkred",
            }}
          >
            {statusMsg}
          </label>
        )}
      </div>
      <div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ width: 30 }}></th>
              <th>Subject</th>
              <th>Start</th>
              <th>End</th>
              <th style={{ width: 10 }}>Time</th>
              <th>Project</th>
              <th>Activity</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev, idx) => (
              <tr key={idx} title={ ev.timesheetExisting ? "Meeting already has an entry on this day. Use checkbox to enter again." : "" } style={{ borderTop: "5px solid #f4f4f4", background: ev.timesheetExisting ? "#bbb" : "#eaeaea" }}>
                <td style={{ textAlign: "center" }} title={ ev.timesheetExisting ? "Override lockout?" : ""}>
                  <input
                    type="checkbox"
                    checked={(selectedMap[idx] || {}).checked || false}
                    onChange={() => toggleChecked(idx)}
                  />
                </td>
                <td>
                  <input
                    disabled={ ev.timesheetExisting && !selectedMap[idx]?.checked }
                    type="text"
                    value={
                      typeof (selectedMap[idx] || {}).subject === "string"
                        ? (selectedMap[idx] || {}).subject
                        : ev.subject || ""
                    }
                    onChange={(e) => setSubjectFor(idx, e.target.value)}
                    style={{
                      width: "100%",
                      minWidth: 200,
                      padding: 6,
                      boxSizing: "border-box",
                    }}
                  />
                </td>
                <td style={{ textAlign: "center" }}>
                  <FormatDateTime dt={ev.start.dateTime} />
                </td>
                <td style={{ textAlign: "center" }}>
                  <FormatDateTime dt={ev.end.dateTime} />
                </td>
                <td style={{ textAlign: "center" }}>
                  {/* compute default hours as fractional hours between start and end */}
                  {(() => {
                    const defaultHours = (() => {
                      try {
                        const s = new Date(ev.start.dateTime);
                        const e = new Date(ev.end.dateTime);
                        if (
                          !s ||
                          !e ||
                          isNaN(s.getTime()) ||
                          isNaN(e.getTime())
                        )
                          return 0;
                        return Math.round(((e - s) / 3600000) * 100) / 100;
                      } catch (e) {
                        return 0;
                      }
                    })();

                    const val =
                      selectedMap[idx] &&
                      typeof selectedMap[idx].hours !== "undefined"
                        ? selectedMap[idx].hours
                        : defaultHours;

                    return (
                      <input
                        disabled={ ev.timesheetExisting && !selectedMap[idx]?.checked }
                        type="number"
                        step="0.01"
                        min="0"
                        value={val}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setHoursFor(
                            idx,
                            isNaN(v) ? 0 : Math.round(v * 100) / 100,
                          );
                        }}
                        style={{ width: 50 }}
                      />
                    );
                  })()}
                </td>
                <td style={{ minWidth: 180 }}>
                  <Typeahead
                    disabled={ ev.timesheetExisting && !selectedMap[idx]?.checked }
                    signal={"RESET:" + selectedWeekStart.toISOString()}
                    items={projects}
                    value={(selectedMap[idx] || {}).project}
                    onChange={(p) => setProjectFor(idx, p)}
                    placeholder="Type project code"
                    getItemKey={(it) => it.code}
                    getItemLabel={(it) => `${it.code} — ${it.desc}`}
                    renderItem={(it) => (
                      <div>
                        <strong style={{ marginRight: 8 }}>{it.code}</strong>
                        <span>{it.desc}</span>
                      </div>
                    )}
                  />
                </td>
                <td style={{ minWidth: 180, paddingRight: 11 }}>
                  <Typeahead
                    disabled={ ev.timesheetExisting && !selectedMap[idx]?.checked }
                    signal={"RESET:" + selectedWeekStart.toISOString()}
                    items={activities}
                    value={(selectedMap[idx] || {}).activity}
                    onChange={(a) => setActivityFor(idx, a)}
                    placeholder="Type activity code"
                    getItemKey={(it) => it.code}
                    getItemLabel={(it) => `${it.code} — ${it.desc}`}
                    renderItem={(it) => (
                      <div>
                        <strong style={{ marginRight: 8 }}>{it.code}</strong>
                        <span>{it.desc}</span>
                      </div>
                    )}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
